import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Card } from "@/components/ui/Card";
import { ButtonShadcn } from "@/components/ui/Button";
import Select from "@/components/ui/Select";
import Switch from "@/components/ui/Switch";
import SegmentedControl from "@/components/ui/SegmentedControl";
import { Badge } from "@/components/ui/Badge";
import { Tabs, TabsList, TabsTab, TabsPanel } from "@/components/ui/Tabs";
import SettingRow from "@/components/shared/SettingRow";
import ConfirmActionModal from "@/components/shared/ConfirmActionModal";
import { CenteredSpinner } from "@/components/shared/Loader";
import { Input } from "@/components/ui/Input";
import toast from "react-hot-toast";
import { TenantSettingsService } from "@/services/tenantSettingsService";
import { currencyOptions } from "@/utils/currencyOptions";
import PermissionGuard from "@/permissions/PermissionGuard";
import { PERMISSIONS } from "@/permissions/constant";
import { hasPermission } from "@/permissions/rbac";
import { useAuth } from "@/hooks/useAuth";
import useLiveResource from "@/hooks/useLiveResource";
import ComplianceSettingsCard from "./ComplianceSettingsCard";
import GuardianSettingsCard from "./GuardianSettingsCard";
import ShiftTimingSettingsCard from "./ShiftTimingSettingsCard";
// Interview Scheduling is disabled — commented out, not deleted. See PLAN
// doc "resilient-stirring-tulip".
// import InterviewSchedulingSettingsCard from "./InterviewSchedulingSettingsCard";
// import InterviewOptionsCard from "./InterviewOptionsCard";
import CalendarTypeSettingsCard from "./CalendarTypeSettingsCard";
import ModuleGate from "@/permissions/ModuleGate";
import { MODULES } from "@/permissions/modules";
import { getErrorMessage, isInsufficientPermissionsError, isModuleUnavailableError } from "@/utils/apiErrorUtils";

// Only the one setting that is not a consent flow. The consent values arrive
// keyed by the flows the response served, and no row is drawn before the
// response lands, so there is nothing here for them to default.
const DEFAULT_SETTINGS = {
  restrictDataVisibility: false,
};

// The flows, their labels, their helper text and which module gates each one all
// come from `emailConsentFlows` on GET /tenant-settings. They used to be written
// out here, and the job form's consent section renders the same three strings —
// two homes for one truth, which would have shown up as two screens describing
// one switch differently. The server owns the copy now; `entry.settingKey` is
// the field on the same response holding that flow's value, and the key the
// PATCH takes back.
//
// Each toggle governs only the *email* step of its flow — the in-app checkbox is
// always shown and always validated server-side. Links already sitting in
// someone's inbox keep working either way: these flags decide whether a link is
// created, never whether an existing one can be used.

// `readOnly` = the viewer holds tenant_config.view but not tenant_config.edit.
// They get the current value as a static label instead of a switch: rendering a
// switch they can only bounce off a 403 reads as a broken page, and a disabled
// switch is indistinguishable from one that's mid-save. The permission check is
// still enforced server-side — this only stops the UI offering an action the
// viewer doesn't have.
const SettingToggle = ({ label, helper, checked, disabled, readOnly, onToggle }) => (
  <SettingRow title={label} description={helper} control={<Switch checked={checked} disabled={disabled} onToggle={onToggle} label={label} readOnly={readOnly} />} />
);

// Telegram channel account — connect the platform's shared bot, or bring
// your own. `canManage` = outreach.manage; without it the card is read-only
// (same reasoning as `readOnly` on SettingToggle above: an action the viewer
// can only bounce off a 403 reads as a broken page, not a permission).

// What each mode means, in the admin's own terms. One string per mode, shown
// under the control for whichever is picked, so the consequence is on screen
// before the click rather than after it.
//
// Labels are the opposite of the internal `value`s on purpose: "Default"
// (value `override`) is the one organization-wide answer, and "Override"
// (value `default`) is what lets a job replace that answer with its own —
// matching how "default" and "override" are used everywhere else (a
// fallback vs. something that replaces it), not the flow's own setting key.
const MODE_COPY = {
  default: "Each job starts from your organization's setting, then keeps its own once it's saved.",
  override: "Set by your organization and applied to every job. Recruiters can't change it on a job.",
};

const MODE_OPTIONS = [
  { value: "default", label: "Job decides" },
  { value: "override", label: "Org decides" },
];

/**
 * One consent flow: the org's value, and who owns it.
 *
 * `SettingToggle` above stays as it is for the data-visibility row, which has
 * one control and no mode. These rows carry two, and they answer two different
 * questions — so they are two components rather than one with a flag.
 *
 * The mode sits in the `note` slot, under the helper, because it qualifies the
 * value rather than competing with it. For a view-only admin it moves to
 * `badges` instead: this page's own reason for the value switch's `readOnly`
 * applies here too — a disabled segmented control is indistinguishable from one
 * mid-save, and the mode is exactly the thing they came to read.
 */
const ConsentSettingRow = ({ flow, checked, enforced, saving, canEdit, onToggleValue, onToggleMode }) => (
  <SettingRow
    title={flow.label}
    description={flow.helper}
    badges={!canEdit ? <Badge label={enforced ? "Org decides" : "Job decides"} title={enforced ? MODE_COPY.override : MODE_COPY.default} /> : null}
    note={
      canEdit ? (
        <div className="flex flex-col gap-1.5">
          <SegmentedControl
            options={MODE_OPTIONS}
            value={enforced ? "override" : "default"}
            onChange={onToggleMode}
            disabled={saving}
            ariaLabel={`Who sets "${flow.label}"`}
          />
          <p className="text-[13px] leading-relaxed text-text-secondary">{enforced ? MODE_COPY.override : MODE_COPY.default}</p>
        </div>
      ) : null
    }
    control={<Switch checked={checked} disabled={saving} onToggle={onToggleValue} label={flow.label} readOnly={!canEdit} />}
  />
);

const OrgSettings = () => {
  const setHeader = useOutletContext();
  const { user } = useAuth();
  // The page itself opens on tenant_config.view OR .edit (see PermissionGuard
  // below), so a view-only user gets in. Only .edit may actually flip a
  // setting — tenant admins and SuperAdmins carry it through the `*` wildcard.
  const canEdit = hasPermission(PERMISSIONS.TENANT_CONFIG_EDIT, user?.permissions);
  // Delegated: lets an admin hand interview-scheduling config to whoever owns
  // it without granting every other org setting. Backend enforces the same
  // narrowing (see tenant_settings/router.py::INTERVIEW_SETTING_KEYS).
  // Interview Scheduling is disabled — commented out, not deleted.
  // const canEditInterviewConfig = hasPermission(PERMISSIONS.INTERVIEW_CONFIG_EDIT, user?.permissions);
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState("");
  const [currencySign, setCurrencySign] = useState("");
  const [savingCurrency, setSavingCurrency] = useState(false);
  // Which key is mid-save, so only that row's switch is disabled.
  const [savingKey, setSavingKey] = useState(null);
  // Pending "turn this off?" confirmation, or null — same shape
  // GuardianSettingsCard uses for its own master switch.
  const [confirmation, setConfirmation] = useState(null);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  // Held apart from `settings` because `normalize` below builds a fixed-key
  // object and would drop an array it does not name.
  const [emailConsentFlows, setEmailConsentFlows] = useState([]);

  useEffect(() => {
    setHeader({
      title: "Org Settings",
      subTitle: "Manage org-wide configuration",
    });
  }, [setHeader]);

  useEffect(() => {
    fetchSettings();
    fetchDefaultCurrency();
  }, []);

  // Two admins can have this page open at once, and the page had no
  // subscription at all — neither saw the other's toggles. `PATCH
  // /tenant-settings` publishes `tenant_config.updated` and bumps
  // `tenants.config_version`; this is the half that acts on it here.
  useLiveResource({
    keys: "tenant_config",
    onChange: () => {
      fetchSettings();
      fetchDefaultCurrency();
    },
  });

  // `restrictDataVisibility` is not a consent flow and stays named here. The
  // consent values are keyed by the flows the response itself served, so a
  // fourth flow needs no change on this page.
  const normalize = (data) => ({
    restrictDataVisibility: Boolean(data.restrictDataVisibility),
    ...Object.fromEntries(
      (data.emailConsentFlows ?? []).flatMap((flow) => [
        [flow.settingKey, Boolean(data[flow.settingKey])],
        // The mode, picked up exactly as the value is. `handleToggle`
        // flips one boolean by key and needs no change for it.
        [flow.enforceSettingKey, Boolean(data[flow.enforceSettingKey])],
      ]),
    ),
  });

  const fetchSettings = async () => {
    // Not `setLoading(true)` again here: this same function re-runs on every
    // `tenant_config.updated` push, including the one this page's own save
    // just published (see `useLiveResource` below). Re-arming `loading` on
    // that echo swapped the whole page — every card, every tab — for the
    // spinner and back for a settings change the admin just made themselves,
    // reading as the page reloading out from under them. `loading` starts
    // `true` and this function is the only place that clears it, so the
    // spinner still covers the one load that has nothing on screen yet: the
    // first mount, before `settings` exists at all.
    try {
      const response = await TenantSettingsService.getSettings();
      if (response?.data) {
        setSettings(normalize(response.data));
        setEmailConsentFlows(response.data.emailConsentFlows ?? []);
      }
    } catch (error) {
      console.error("Failed to load settings:", error);
      toast.error("Failed to load organization settings.");
    } finally {
      setLoading(false);
    }
  };

  const fetchDefaultCurrency = async () => {
    try {
      const result = await TenantSettingsService.getDefaultCurrency();
      setCurrency(result.currency || "");
      setCurrencySign(result.currencySign || "");
    } catch (error) {
      console.error("Failed to load default currency:", error);
    }
  };

  const handleToggle = async (key) => {
    const newValue = !settings[key];
    try {
      setSavingKey(key);
      const response = await TenantSettingsService.updateSettings({
        [key]: newValue,
      });
      if (response?.data) {
        setSettings(normalize(response.data));
        // The PATCH response carries the registry too, so the rows
        // survive the save. Without this, normalize's fixed keys would
        // have been fine and the array simply gone.
        setEmailConsentFlows(response.data.emailConsentFlows ?? []);
      } else {
        setSettings((current) => ({ ...current, [key]: newValue }));
      }
      toast.success("Settings updated successfully.");
    } catch (error) {
      console.error("Failed to update settings:", error);
      toast.error("Failed to update organization settings.");
    } finally {
      setSavingKey(null);
    }
  };

  // Confirm on the way OFF only, same rule as the Guardian card: turning a
  // setting on is never destructive, so only the off-click ever pauses for a
  // "are you sure" — otherwise every admin session would end in reflexive
  // "yes, yes, yes" clicks that stop meaning anything.
  const requestToggle = (key, warning) => {
    const turningOff = settings[key];
    if (turningOff && warning) {
      setConfirmation({ ...warning, onConfirm: () => handleToggle(key) });
      return;
    }
    handleToggle(key);
  };

  const handleCurrencyChange = async (code) => {
    if (!code || code === currency) return;
    const selected = currencyOptions.find((opt) => opt.value === code);
    setCurrency(code);
    setCurrencySign(selected?.symbol || "");

    try {
      setSavingCurrency(true);
      await TenantSettingsService.updateDefaultCurrency({
        currency: code,
        currency_sign: selected?.symbol || "",
      });
      toast.success("Default currency updated.");
    } catch (error) {
      console.error("Failed to update default currency:", error);
      toast.error("Failed to update default currency.");
      fetchDefaultCurrency();
    } finally {
      setSavingCurrency(false);
    }
  };

  if (loading) {
    return <CenteredSpinner />;
  }

  return (
    <PermissionGuard permissions={[PERMISSIONS.TENANT_CONFIG_VIEW, PERMISSIONS.TENANT_CONFIG_EDIT]}>
      <div className="w-full flex flex-col gap-4">
        {!canEdit && (
          <p className="max-w-3xl text-[13px] text-text-secondary">You can view these settings but not change them. Ask an administrator if one needs updating.</p>
        )}

        {/* Grouped into tabs so a change to one setting no longer means
            scrolling past a dozen unrelated cards to find it — see
            .claude/rules/frontend-ux.md on a page that fetches its own data
            owing a reader something better than one long column. */}
        <Tabs defaultValue="general">
          <TabsList>
            <TabsTab value="general">General</TabsTab>
            <TabsTab value="consent">Consent & Compliance</TabsTab>
            <TabsTab value="scheduling">Scheduling</TabsTab>
          </TabsList>

          <TabsPanel value="general" className="gap-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="p-6 bg-background-secondary border border-border-primary rounded-xl">
                <div className="flex flex-col gap-6">
                  <div className="flex flex-col gap-1.5">
                    <h2 className="text-lg font-semibold text-text-primary">Data Visibility</h2>
                    <p className="text-[13px] text-text-secondary leading-relaxed">
                      Control whether employees can see all jobs and candidates across the tenant, or only those they are directly involved with.
                    </p>
                  </div>

                  <SettingToggle
                    label="Restrict data visibility"
                    helper="When enabled, employees will only see jobs they created, jobs assigned to them, and jobs belonging to their reporting subtree. Candidates are only visible if their corresponding job is visible."
                    checked={settings.restrictDataVisibility}
                    disabled={savingKey !== null}
                    readOnly={!canEdit}
                    onToggle={() =>
                      requestToggle("restrictDataVisibility", {
                        title: 'Turn off "Restrict data visibility"?',
                        lines: [
                          "Every employee will be able to see every job and candidate across the tenant, not just the ones they created, are assigned to, or fall under their reporting subtree.",
                          "This takes effect immediately, tenant-wide.",
                        ],
                      })
                    }
                  />
                </div>
              </Card>

              <Card className="p-6 bg-background-secondary border border-border-primary rounded-xl">
                <div className="flex flex-col gap-6">
                  <div className="flex flex-col gap-1.5">
                    <h2 className="text-lg font-semibold text-text-primary">Default Currency</h2>
                    <p className="text-[13px] text-text-secondary leading-relaxed">
                      Set the currency suggested for new job postings. Job editors can still choose a different currency where appropriate.
                    </p>
                  </div>

                  <div className="border border-border-primary/60 rounded-lg p-5">
                    <label className="block text-sm mb-1.5 font-medium text-text-secondary">Currency</label>
                    <Select
                      value={currency}
                      onChange={handleCurrencyChange}
                      options={currencyOptions}
                      placeholder="Select currency"
                      disabled={!canEdit || savingCurrency}
                      searchable
                      searchPlaceholder="Search currency..."
                    />
                    {currency && (
                      <p className="text-xs text-text-secondary mt-2">
                        Current default: {currencySign ? `${currencySign} ` : ""}
                        {currency}
                      </p>
                    )}
                    {savingCurrency && <p className="text-xs text-text-secondary mt-2">Saving...</p>}
                  </div>
                </div>
              </Card>
            </div>
          </TabsPanel>

          <TabsPanel value="consent" className="gap-4">
            <Card className="p-6 bg-background-secondary border border-border-primary rounded-xl">
              <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-1.5">
                  <h2 className="text-lg font-semibold text-text-primary">Consent Confirmation Emails</h2>
                  <p className="text-[13px] text-text-secondary leading-relaxed">
                    Each flow below asks candidates to confirm by email before the action goes through — on top of the checkbox they already ticked in the app.
                    Confirmation links already sent keep working either way.
                  </p>
                </div>

                {emailConsentFlows.map((flow) => {
                  const toggle = (
                    <ConsentSettingRow
                      key={flow.settingKey}
                      flow={flow}
                      checked={settings[flow.settingKey]}
                      enforced={settings[flow.enforceSettingKey]}
                      saving={savingKey !== null}
                      canEdit={canEdit}
                      onToggleValue={() =>
                        requestToggle(flow.settingKey, {
                          title: `Turn off "${flow.label}"?`,
                          lines: [
                            flow.helper,
                            "The in-app checkbox stays and is still validated — only this flow's confirmation email stops going out. Links already sent keep working.",
                          ],
                        })
                      }
                      onToggleMode={() => handleToggle(flow.enforceSettingKey)}
                    />
                  );
                  // A setting about a flow the tenant does not own
                  // explains nothing and invites a support ticket.
                  // `w-full`: the toggle row spans the card, so the
                  // disabled wrapper has to as well.
                  return flow.module ? (
                    <ModuleGate key={flow.settingKey} module={flow.module} className="w-full">
                      {toggle}
                    </ModuleGate>
                  ) : (
                    toggle
                  );
                })}
              </div>
            </Card>

            {/* No module gate. `guardian_settings` stopped being a module on
                    2026-08-10: every tenant has this card, and the platform's
                    prompt-safety policy decides whether they own the switch on
                    it. The card renders it locked, with the reason, when they do
                    not — which is more than the module could say. */}
            <GuardianSettingsCard canEdit={canEdit} />

            {/* Compliance policy (Phase 9 — moved here from the Checklists
                    page, which was only ever its temporary home). No gate here:
                    the card carries its own COMPLIANCE_MANAGE + module guards,
                    because Org Settings opens on tenant_config permissions and
                    those are held by different people. */}
            <ComplianceSettingsCard />
          </TabsPanel>

          <TabsPanel value="scheduling" className="gap-4">
            <ShiftTimingSettingsCard canEdit={canEdit} />

            {/* Interview Scheduling is disabled — commented out, not
                deleted. See PLAN doc "resilient-stirring-tulip".
            <ModuleGate module={MODULES.INTERVIEW_SCHEDULING}>
              <InterviewSchedulingSettingsCard canEdit={canEdit || canEditInterviewConfig} />
              {["duration", "mode", "type", "round"].map((category) => (
                <InterviewOptionsCard key={category} category={category} canEdit={canEdit || canEditInterviewConfig} />
              ))}
            </ModuleGate>
            */}
            <CalendarTypeSettingsCard canEdit={canEdit} />
          </TabsPanel>
        </Tabs>
      </div>

      <ConfirmActionModal confirmation={confirmation} onClose={() => setConfirmation(null)} actionText="Turn off" />
    </PermissionGuard>
  );
};

export default OrgSettings;
